import { useState, useEffect, useMemo, useCallback } from "react";
import { toast } from "react-toastify";
import { useDebounce } from "use-debounce";

import apiClient from "../../../application/apis/tenant-management/tenant-management-client";
import { Group, GroupRole } from "../../../application/domain/group/group";
import { toTotalPage } from "../../../application/domain/pagination/paging";
import { reportUnexpectedError } from "../../../error-reporter";

const MAX_CANDIDATE_GROUP_LIMIT = 50;

export const useGroupSelector = ({
  editingGroupRoles,
  setGroupRoles,
  orgId,
  isServiceProvider,
}: {
  editingGroupRoles: { [key: string]: GroupRole };
  setGroupRoles: (groupRoles: { [key: string]: GroupRole }) => void;
  orgId: string;
  isServiceProvider: boolean;
}) => {
  // TODO(adshidtadka): stagingにおけるグループ未所属の一般ユーザーをマイグレーションした後削除する
  // https://graffer.atlassian.net/browse/PRODUCTS-10896
  const isGroupsRoles = useMemo(
    () => Object.keys(editingGroupRoles).length > 0,
    [editingGroupRoles],
  );

  const addGroup = useCallback(() => {
    if (Object.keys(editingGroupRoles).some((k) => k === "")) {
      return;
    }
    setGroupRoles({
      ...editingGroupRoles,
      "": "Staff", // グループ名が未指定な状態でstateを生成し、テナントユーザーに選択を促す
    });
  }, [editingGroupRoles, setGroupRoles]);

  const [page, setPage] = useState<number>(1);
  const [totalPage, setTotalPage] = useState<number>(1);
  const [name, setName] = useState<string>("");
  const [bouncedName] = useDebounce(name, 500);
  const [editingGroups, setEditingGroups] = useState<Group[]>([]);
  const [searchedGroups, setSearchedGroups] = useState<Group[]>([]);

  const isDisplayBackButton = useMemo(() => {
    return page !== 1;
  }, [page]);
  const isDisplayNextButton = useMemo(() => {
    return totalPage > page;
  }, [totalPage, page]);

  const fetchGroups = useCallback(
    async (page: number, filter: { ids?: string[]; name?: string }) => {
      if (isServiceProvider) {
        // note: ServiceProviderはテナントに所属していないので、backendでorgIdの解決ができない。そのため、APIを呼び分ける必要がある
        return await apiClient.fetchGroupsByOrganizationId(
          orgId,
          page,
          MAX_CANDIDATE_GROUP_LIMIT,
          filter,
        );
      }

      return await apiClient.fetchGroups(
        page,
        MAX_CANDIDATE_GROUP_LIMIT,
        filter,
      );
    },
    [orgId, isServiceProvider],
  );

  // すでに設定されているグループの情報を取得。
  // グループ名などを表示してあげるために必要。
  const fetchEditingGroups = useCallback(async () => {
    const ids = Object.keys(editingGroupRoles).filter((s) => s !== "");
    if (ids.length === 0) {
      setEditingGroups([]);
      return;
    }

    try {
      const { data } = await fetchGroups(
        // ids を指定するので、名称に依る検索で用いる `page` 変数への参照は不要。
        // 本来的には editingGroupRoles のキーに基づく内容をすべて取得しきるまで
        // ここで指定するページを increment するループが必要。
        // MAX_CANDIDATE_GROUP_LIMIT >= ids.length ならば1で問題ない。
        // cf. https://github.com/graffer-inc/tenant-management-module/blob/bd688abed5054a810972b7a153191016b7ad778f/src/domain/users/user.go#L32
        // > GroupMaxCount = 30
        1,
        {
          ids,
        },
      );
      setEditingGroups(data);
    } catch (e) {
      reportUnexpectedError(e);
      toast.error("グループ一覧の取得に失敗しました。");
    }
  }, [editingGroupRoles, fetchGroups]);

  // 文字列からグループを検索して取得。
  const searchGroups = useCallback(async () => {
    try {
      const { data, headers } = await fetchGroups(page, {
        name: bouncedName,
      });
      setSearchedGroups(data);
      setTotalPage(
        toTotalPage(
          Number(headers["pagination-count"]),
          MAX_CANDIDATE_GROUP_LIMIT,
        ),
      );
    } catch (e) {
      reportUnexpectedError(e);
      toast.error("グループ一覧の取得に失敗しました。");
    }
  }, [bouncedName, fetchGroups, page]);

  useEffect(() => {
    fetchEditingGroups();
  }, [fetchEditingGroups]);
  useEffect(() => {
    searchGroups();
  }, [searchGroups]);

  // ユーザーが入力欄で目にすることになる、選択可能なグループの候補情報。
  const candidateGroups = useMemo(() => {
    const groupsMap = new Map<string, Group>();
    editingGroups.forEach((g) => groupsMap.set(g.id!, g));
    searchedGroups.forEach((g) => groupsMap.set(g.id!, g));
    return groupsMap;
  }, [editingGroups, searchedGroups]);

  return {
    isGroupsRoles,
    addGroup,
    page,
    setPage,
    isDisplayBackButton,
    isDisplayNextButton,
    setName,
    candidateGroups,
  };
};
